<template>
   <div data-cy="public-flow-invitation-v1" style="min-height: 8rem">
      <RichTextRenderer v-if="block.data.content" data-cy="richtext-content" :document="block.data.content" />

      <TextBox
         id="email"
         v-model.trim="state.currentAnswer.email"
         class="mt-6"
         :autoFocus="true"
         type="email"
         :label="$t('validation.email.value')"
         :hasError="showBlockErrors && $v.$error"
         :errorHelper="helperData"
         :placeholder="$t('validation.email.placeholder')"
         :disabled="!!props.userBlockAnswer?.data?.email"
         @input="processInputs"
      />
   </div>
</template>

<script setup lang="ts">
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useVuelidate } from "@vuelidate/core";
import { email, required, helpers } from "@vuelidate/validators";

export interface PublicFlowInviteAnswerData {
   email: string;
}

export interface PublicFlowInviteBlock extends FlowselBlock {
   data: {
      content: any;
   };
}
interface Props {
   block: PublicFlowInviteBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}
interface State {
   currentAnswer: PublicFlowInviteAnswerData;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const emit = defineEmits(["update-answer", "update-valid"]);
const props = defineProps<Props>();

const state = reactive<State>({
   currentAnswer: {
      email: props.userBlockAnswer?.data?.email || "",
   },
});

// Validation
// -----------------------------------------
const rules = {
   currentAnswer: {
      email: {
         ...(props.block.isRequired && { required: helpers.withMessage($t("validation.email.required"), required) }),
         email: helpers.withMessage($t("validation.email.invalid"), email),
      },
   },
};
const $v: any = useVuelidate(rules, state);

// Computed
// -----------------------------------------
const helperData = computed(() => {
   return $v.value.$error
      ? {
           text: $v.value.$errors[0].$message,
           code: $v.value.$errors[0].$validator,
        }
      : null;
});

// Methods
// -----------------------------------------
function processInputs() {
   if (!$v.value.$dirty) {
      $v.value.$touch();
   }

   if (($v.value.$error || $v.value.$invalid) && !props.userBlockAnswer?.data?.email) {
      emit("update-valid", false);
   } else {
      emit("update-valid", true);
      emit("update-answer", state.currentAnswer);
   }
}

processInputs();
</script>
